'use client'

import { useEffect, useRef, useState } from 'react'

const IDLE_CALLBACK_TIMEOUT_MS = 2500
// Must match `XL_BREAKPOINT` in CursorReveal (effect only renders when innerWidth > this).
const CURSOR_REVEAL_MIN_WIDTH_PX = 1440

// Load CursorReveal only after main thread is idle to reduce parse/compile/execute blocking (TBT).
// Skip entirely on smaller viewports where CursorReveal returns null (smaller JS on typical mobile runs).
export default function CursorRevealLazy() {
  const [CursorReveal, setCursorReveal] = useState<React.ComponentType | null>(null)
  const loadRequestedRef = useRef(false)

  useEffect(() => {
    let idleId: ReturnType<typeof requestIdleCallback> | undefined
    let timeoutId: number | undefined

    const scheduleLoad = () => {
      if (typeof window === 'undefined') return
      if (window.innerWidth <= CURSOR_REVEAL_MIN_WIDTH_PX) return
      if (loadRequestedRef.current) return
      loadRequestedRef.current = true

      const load = () => {
        import('@/components/CursorReveal').then((m) => setCursorReveal(() => m.default))
      }
      if (typeof requestIdleCallback !== 'undefined') {
        idleId = requestIdleCallback(load, { timeout: IDLE_CALLBACK_TIMEOUT_MS })
      } else {
        timeoutId = window.setTimeout(load, 100)
      }
    }

    scheduleLoad()
    window.addEventListener('resize', scheduleLoad)
    return () => {
      window.removeEventListener('resize', scheduleLoad)
      if (idleId !== undefined && typeof cancelIdleCallback !== 'undefined') {
        cancelIdleCallback(idleId)
      }
      if (timeoutId !== undefined) window.clearTimeout(timeoutId)
    }
  }, [])

  if (!CursorReveal) return null
  return <CursorReveal />
}
